import { useEffect, useState } from "react";
import { ProductCard } from "@/components/ProductCard";
import { WaitlistForm } from "@/components/WaitlistForm";
import { getPreorders } from "@/lib/api";
import { Clock, PackageCheck, Bell } from "lucide-react";
import { HoundNote } from "@/components/HoundNote";

export default function PreOrders() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    getPreorders().then(setProducts).catch(() => {});
  }, []);

  return (
    <div>
      <section className="bg-subtle py-16">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <p className="text-xs uppercase tracking-[0.25em] text-rosegold mb-3">Reserve yours</p>
          <h1 className="font-display text-4xl sm:text-5xl text-maroon">Pre-Orders</h1>
          <p className="mt-5 text-inkbrown/75 leading-relaxed max-w-2xl mx-auto">
            Secure your favourite pieces before full stock arrives. Register your interest and pay
            upfront through our secure Shopify checkout, we'll do the rest.
          </p>
        </div>
      </section>

      {/* How pre-orders work */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-14">
        <div className="grid sm:grid-cols-3 gap-6">
          {[
            { icon: Bell, t: "1. Reserve", s: "Choose your size, colour and quantity, then pay upfront to lock in your piece." },
            { icon: Clock, t: "2. We order", s: "Orders are placed with our supplier once the pre-order window closes." },
            { icon: PackageCheck, t: "3. We dispatch", s: "Estimated dispatch times are shared clearly and updated along the way." },
          ].map((s) => (
            <div key={s.t} className="rounded-3xl bg-white p-8 border border-rosegold/15">
              <s.icon className="w-6 h-6 text-rosegold mb-4" strokeWidth={1.5} />
              <p className="font-display text-xl text-maroon">{s.t}</p>
              <p className="mt-2 text-sm text-inkbrown/70 leading-relaxed">{s.s}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-3xl bg-maroon/5 border border-maroon/15 p-6 text-center">
          <p className="text-sm text-inkbrown/80 leading-relaxed max-w-3xl mx-auto">
            <strong className="text-maroon">Please note:</strong> These items are available for pre-order.
            Orders will be placed with our supplier once the pre-order window closes.
            Estimated dispatch times will be clearly communicated.
          </p>
        </div>

        <HoundNote
          author="Henry"
          message="Good things come to hounds who wait. Reserve yours now and I promise to only chew it a little before it ships."
          className="mx-auto mt-8 rotate-1"
        />
      </section>

      {/* Pre-order products */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20">
        {products.length === 0 ? (
          <p className="text-center text-inkbrown/50 py-10">No open pre-orders right now, join the list below.</p>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-8" data-testid="preorder-grid">
            {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        )}
      </section>

      {/* Waitlist */}
      <section className="bg-subtle py-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="font-display text-3xl text-maroon">Join the pre-order list</h2>
          <p className="mt-4 text-inkbrown/70">Get notified the moment a new pre-order window opens.</p>
          <div className="mt-8 flex justify-center">
            <WaitlistForm source="preorder-list" />
          </div>
        </div>
      </section>
    </div>
  );
}
